/**
 * Canonical alert preference data layer (#231). Pure functions over
 * localStorage — no React. The hook wrapper is `useAlertPreferences`.
 *
 * Storage: the SAME `galactus:soundSettings` key the legacy layer uses — one
 * key, one cross-tab `storage` event, no second store. The payload keeps
 * every legacy flat field live (mirrored on write) and adds `schemaVersion`
 * + `channels`. Nothing is ever deleted from the stored object: unknown
 * top-level keys and unknown per-channel keys ride through read-modify-write
 * untouched. That is the #233 extension contract — new per-channel fields
 * (visualEnabled, delivery thresholds) round-trip before they are typed.
 *
 * Migration is read-only: a missing channel entry derives its soundEnabled
 * from the channel's legacy flat boolean (the queue trio all derive from
 * `queueFrontSoundEnabled` — deterministic fan-out for stored true AND
 * false), falling back to registry defaults. No write-back on load, so an
 * old-code tab's writes keep being honored for as long as no canonical
 * write happens.
 */

import {
  ALERT_CHANNEL_IDS,
  ALERT_REGISTRY,
  type AlertChannelId,
  type AlertChannelSettings,
} from './registry';
import {
  clampAgainstSibling,
  normalizeDeliveryChannels,
  normalizeThreshold,
  type DeliveryChannelsInput,
} from './deliveryThresholds';

/** Same key as the legacy layer — deliberately NOT channel-namespaced. */
export const STORAGE_KEY = 'galactus:soundSettings';

/**
 * Stamped on every canonical write, deliberately NOT read back. Migration is
 * inference-based (channel entry present -> use it, else the legacy flat key),
 * which stays correct even when an old-code tab strips the field. This is a
 * forward marker for a future breaking reshape; nothing gates on it today.
 */
export const ALERT_SCHEMA_VERSION = 3;

export interface AlertPreferences {
  /** Stored under the legacy top-level name `volume`; multiplicative master. */
  masterVolume: number;
  /** Minimum contracts in a fill to trigger sound (0 = no minimum). */
  minFillContracts: number;
  channels: Record<AlertChannelId, AlertChannelSettings>;
}

function defaultChannels(): Record<AlertChannelId, AlertChannelSettings> {
  const channels = {} as Record<AlertChannelId, AlertChannelSettings>;
  for (const id of ALERT_CHANNEL_IDS) channels[id] = { ...ALERT_REGISTRY[id].defaults };
  return channels;
}

/**
 * Deep-frozen: this is exported and #232 will import it, so a consumer
 * mutating a nested channel object would poison the process-wide default.
 * Callers wanting a mutable baseline use `freshDefaults()` via the read path.
 */
export const ALERT_PREFERENCE_DEFAULTS: Readonly<AlertPreferences> = Object.freeze({
  masterVolume: 0.5,
  minFillContracts: 0,
  channels: Object.freeze(
    Object.fromEntries(
      ALERT_CHANNEL_IDS.map((id) => [id, Object.freeze({ ...ALERT_REGISTRY[id].defaults })])
    )
  ) as Record<AlertChannelId, AlertChannelSettings>,
});

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function freshDefaults(): AlertPreferences {
  return {
    masterVolume: ALERT_PREFERENCE_DEFAULTS.masterVolume,
    minFillContracts: ALERT_PREFERENCE_DEFAULTS.minFillContracts,
    channels: defaultChannels(),
  };
}

export function readAlertPreferences(): AlertPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return freshDefaults();
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return freshDefaults();

    const channelsRaw = isPlainObject(parsed.channels) ? parsed.channels : undefined;
    const channels = {} as DeliveryChannelsInput;
    for (const id of ALERT_CHANNEL_IDS) {
      const meta = ALERT_REGISTRY[id];
      const storedRaw = channelsRaw?.[id];
      const stored = isPlainObject(storedRaw) ? storedRaw : undefined;
      const legacyRaw = meta.legacyKey === null ? undefined : parsed[meta.legacyKey];
      const legacyVal = typeof legacyRaw === 'boolean' ? legacyRaw : undefined;
      const soundEnabled =
        typeof stored?.soundEnabled === 'boolean'
          ? stored.soundEnabled
          : (legacyVal ?? meta.defaults.soundEnabled);
      const volume =
        typeof stored?.volume === 'number' && Number.isFinite(stored.volume)
          ? clamp01(stored.volume)
          : meta.defaults.volume;
      // Spread stored first: unknown per-channel keys (#233 fields) survive
      // in memory and therefore survive the next write.
      channels[id] = { ...stored, soundEnabled, volume };
    }

    const masterVolume =
      typeof parsed.volume === 'number' && Number.isFinite(parsed.volume)
        ? clamp01(parsed.volume)
        : ALERT_PREFERENCE_DEFAULTS.masterVolume;
    const minFillContracts =
      typeof parsed.minFillContracts === 'number' && Number.isFinite(parsed.minFillContracts)
        ? Math.max(0, Math.floor(parsed.minFillContracts))
        : ALERT_PREFERENCE_DEFAULTS.minFillContracts;

    // Delivery fields last: each channel is clamped against its OWN bounds and
    // filled from registry defaults. Non-destructive and never cross-channel —
    // ordering between a warning/critical pair is a SETTER concern (#233).
    return { masterVolume, minFillContracts, channels: normalizeDeliveryChannels(channels) };
  } catch {
    return freshDefaults();
  }
}

function normalizeMinFill(v: number): number {
  return Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
}

type AlertPreferencesListener = () => void;
const listeners = new Set<AlertPreferencesListener>();

/**
 * Same-tab change notification. The `storage` event fires ONLY in other tabs,
 * so with two hooks over this one key (legacy `useSoundSettings` and canonical
 * `useAlertPreferences`) a write from one leaves the other holding a stale
 * snapshot — which it then writes back, silently reverting the change. Every
 * successful `writeAlertPreferences` fires this; cross-tab sync stays on the
 * `storage` event. Returns an unsubscribe.
 */
export function subscribeAlertPreferences(fn: AlertPreferencesListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notifyAlertPreferencesChanged(): void {
  // Snapshot: a listener may unsubscribe during iteration. One throwing
  // subscriber must not take down the others or the write path.
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {
      // subscriber fault is not the writer's problem
    }
  }
}

/**
 * Legacy flat booleans derived from the canonical channels, written alongside
 * them so a mixed-version sibling tab (old code during rollout) keeps reading
 * truth. `queueFrontSoundEnabled` mirrors as OR of the queue trio: if ANY
 * queue channel still sounds, an old-code tab must keep its shared gate open.
 */
function legacyMirror(prefs: AlertPreferences): Record<string, boolean> {
  const mirror: Record<string, boolean> = {};
  for (const id of ALERT_CHANNEL_IDS) {
    const meta = ALERT_REGISTRY[id];
    // Null legacyKey = no flat boolean to mirror to (#233 velocity channels).
    // Without this guard the mirror grows a literal "null" key.
    if (meta.legacyKey === null || meta.legacyKey === 'queueFrontSoundEnabled') continue;
    mirror[meta.legacyKey] = prefs.channels[id].soundEnabled;
  }
  mirror.queueFrontSoundEnabled =
    prefs.channels.queueWarning.soundEnabled ||
    prefs.channels.queueCritical.soundEnabled ||
    prefs.channels.fellBehind.soundEnabled;
  return mirror;
}

/**
 * Lossless read-modify-write: spreads the currently stored object (and each
 * stored channel object) under the new values, so unknown keys are never
 * dropped. Storage errors are swallowed to match the legacy layer.
 */
export function writeAlertPreferences(next: AlertPreferences): void {
  let wrote = false;
  try {
    let rawParsed: Record<string, unknown> = {};
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      if (isPlainObject(parsed)) rawParsed = parsed;
    } catch {
      // corrupt existing value: overwrite from scratch
    }
    const rawChannels = isPlainObject(rawParsed.channels) ? rawParsed.channels : {};

    const channels: Record<string, unknown> = { ...rawChannels };
    for (const id of ALERT_CHANNEL_IDS) {
      const storedRaw = rawChannels[id];
      const stored = isPlainObject(storedRaw) ? storedRaw : undefined;
      const meta = ALERT_REGISTRY[id];
      const nextCh = next.channels[id];
      // Known fields are written EXPLICITLY rather than by spreading the whole
      // in-memory channel. The read path materializes visualEnabled /
      // deliveryThreshold on every channel so callers get a total type; a
      // wholesale spread would persist them onto all 18, which is noise now and
      // a trap later — a stored value beats a future registry default, so a
      // stray `null` would outrank a default the product later changes.
      // `...stored` still carries unknown keys through (#231 losslessness).
      channels[id] = {
        ...stored,
        soundEnabled: nextCh.soundEnabled,
        volume: Number.isFinite(nextCh.volume) ? clamp01(nextCh.volume) : meta.defaults.volume,
        ...(meta.visual ? { visualEnabled: nextCh.visualEnabled } : {}),
        ...(meta.threshold !== null ? { deliveryThreshold: nextCh.deliveryThreshold } : {}),
      };
    }

    const payload = {
      ...rawParsed,
      ...legacyMirror(next),
      schemaVersion: ALERT_SCHEMA_VERSION,
      volume: Number.isFinite(next.masterVolume)
        ? clamp01(next.masterVolume)
        : ALERT_PREFERENCE_DEFAULTS.masterVolume,
      minFillContracts: normalizeMinFill(next.minFillContracts),
      channels,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    wrote = true;
  } catch {
    // localStorage full or unavailable
  }
  // Outside the try: a failed write changed nothing, and a throwing subscriber
  // must not be mistaken for a storage failure.
  if (wrote) notifyAlertPreferencesChanged();
}

/** Patch master fields; returns the persisted snapshot (fresh read). */
export function updateAlertPreferences(
  patch: Partial<Pick<AlertPreferences, 'masterVolume' | 'minFillContracts'>>
): AlertPreferences {
  const next = { ...readAlertPreferences(), ...patch };
  writeAlertPreferences(next);
  return readAlertPreferences();
}

/** Patch one channel; returns the persisted snapshot (fresh read). */
export function setChannelSettings(
  id: AlertChannelId,
  patch: Partial<AlertChannelSettings>
): AlertPreferences {
  const next = readAlertPreferences();
  next.channels[id] = { ...next.channels[id], ...patch };
  writeAlertPreferences(next);
  return readAlertPreferences();
}

/**
 * Toggle a channel's visual delivery. Independent of `soundEnabled` — muting
 * one must never suppress the other. No-op in storage for channels with no
 * visual capability (the write path drops the key).
 */
export function setChannelVisualEnabled(id: AlertChannelId, enabled: boolean): AlertPreferences {
  return setChannelSettings(id, { visualEnabled: enabled });
}

/**
 * Set a channel's delivery threshold. Clamps to the channel's own bounds, then
 * holds it strictly clear of its ordered sibling — clamping ONLY the value
 * being written. The sibling's stored value is read and never modified.
 *
 * The sibling snapshot comes from a FRESH read, not from any caller-held
 * object, so a stale UI snapshot cannot clamp against an outdated pair.
 *
 * Returns the persisted snapshot: the stored value may differ from the value
 * passed in, and #232 must re-read `result.channels[id].deliveryThreshold` to
 * display what actually landed.
 */
export function setChannelDeliveryThreshold(
  id: AlertChannelId,
  threshold: number
): AlertPreferences {
  const meta = ALERT_REGISTRY[id].threshold;
  if (meta === null) return readAlertPreferences();
  const current = readAlertPreferences();
  const normalized = normalizeThreshold(
    threshold,
    meta,
    ALERT_REGISTRY[id].defaults.deliveryThreshold ?? meta.min
  );
  return setChannelSettings(id, {
    deliveryThreshold: clampAgainstSibling(id, normalized, current.channels),
  });
}

/** First-class order-placed setter (issue #231 requirement 4). */
export function setOrderPlacedSoundEnabled(enabled: boolean): AlertPreferences {
  return setChannelSettings('orderPlaced', { soundEnabled: enabled });
}

export function isChannelSoundEnabled(id: AlertChannelId): boolean {
  return readAlertPreferences().channels[id].soundEnabled;
}

/**
 * The per-channel multiplier ALONE. Emitters that route through
 * `audioContext` (which already applies master volume internally) must use
 * THIS as their amplitude multiplier — using `getEffectiveVolume` there
 * would apply master twice.
 */
export function getChannelVolume(id: AlertChannelId): number {
  return readAlertPreferences().channels[id].volume;
}

/** masterVolume x channel multiplier — for consumers that mix from scratch. */
export function getEffectiveVolume(id: AlertChannelId): number {
  const prefs = readAlertPreferences();
  return prefs.masterVolume * prefs.channels[id].volume;
}
